import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useFormik} from 'formik';
import {Grid} from '@material-ui/core';
import DialogContainer, {DialogButton} from '../../../components/AlertDialog/DialogContainer';
import TextField from '../../../components/TextField/TextField';
import IconWithTooltip from '../../../components/Tooltip/IconWithTooltip';
import MainProductSelectField from '../../../components/Extensions/MainProductSelectFiled/MainProductSelectField';
import {MainProductType} from '../../../components/Extensions/MainProductSelectFiled/utils';
import KeyboardDateTimePicker from '../../../components/KeyboardDateTimePicker/KeyboardDateTimePicker';
import CreateNewExtensionErrorDialog from '../../../components/Extensions/CreateNewExtensionErrorDialog';
import {actions} from '../../../store';
import {ReduxState} from '../../../store/types';
import {AccountIdFaultKind, getAccountFaultKind} from '../../../store/types/Account';
import {
    accountEmailMaxLength,
    accountIdMaxLength,
    makeAddAccountValidationSchema,
    AddNewAccountFormValues,
    initialValues,
    useStyles,
} from './AddNewAccountDialog.utils';
import dayjs from '../../../services/customDayJs';

interface AddNewAccountDialogProps {
    isOpen: boolean;
    toggleVisibility: () => void;
    onSuccess: () => void;
}

const AddNewAccountDialog: React.VFC<AddNewAccountDialogProps> = ({isOpen, toggleVisibility, onSuccess}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const classes = useStyles();

    const [fetchData, setFetchData] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const {isAdding, addErrors} = useSelector((state: ReduxState) => state.accounts);
    const userDateFormat = useSelector<ReduxState, string>(
        (state) =>
            state.generic.globalCustomerInfo?.customer_info?.out_date_format || '',
    );
    const validationSchema = useMemo(
        () => makeAddAccountValidationSchema(userDateFormat),
        [userDateFormat],
    );

    const {
        values,
        handleChange,
        handleSubmit,
        resetForm,
        setFieldValue,
        setFieldError,
        errors,
        setErrors,
    } = useFormik<AddNewAccountFormValues>({
        initialValues,
        validationSchema,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: (form) => {
            dispatch(
                actions.addAccount.request({
                    accountId: form.accountId,
                    email: form.email,
                    i_product: form.mainProductId!,
                    activationDate: form.activationDate ?? undefined,
                    successCallback: () => {
                        resetForm();
                        onSuccess();
                    },
                    errorCallback: () => {
                        toggleVisibility();
                        resetForm();
                        setShowErrorDialog(true);
                    },
                }),
            );
        },
    });

    useEffect(() => {
        if (isOpen) {
            resetForm();
            dispatch(actions.clearAddAccountErrors());
            setFetchData(true);
        } else {
            setFetchData(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!addErrors) {
            setErrors({});
            return;
        }

        switch (getAccountFaultKind(addErrors)) {
            case AccountIdFaultKind.DuplicateId:
                return setFieldError('accountId', t('errors:accounts.duplicateId'));
            case AccountIdFaultKind.ForbiddenCharacter:
                return setFieldError('accountId', t('errors:portalUsers.portalRegex'));
            case AccountIdFaultKind.LimitExceeded:
                return setFieldError('mainProductId', t('errors:accounts.limitExceeded'));
        }
    }, [addErrors]);

    const minDate = dayjs().toDate();
    const isDisabled = !values.accountId || !values.mainProductId;

    return (
        <>
            <DialogContainer
                isOpen={isOpen}
                header={t('screens:accounts.addNewAccount')}
                headerClass={classes.header}
                className={classes.modalContainer}
                showLoader={isAdding}
                dialogActionsButtons={[
                    <DialogButton
                        key="cancel"
                        label={t('common:cancel')}
                        onClick={() => {
                            resetForm();
                            toggleVisibility();
                        }}
                    />,
                    <DialogButton
                        key="save"
                        label={t('common:save')}
                        onClick={handleSubmit}
                        primary
                        disabled={isDisabled}
                        loadingAnimation={isAdding}
                    />,
                ]}
            >
                <form onSubmit={handleSubmit} autoComplete="off">
                    <Grid item className={classes.itemsContainer}>
                        <TextField
                            id="accountId"
                            label={t('screens:accounts.accountId')}
                            value={values.accountId}
                            onChange={handleChange}
                            setFieldError={setFieldError}
                            helperText={errors.accountId}
                            required
                            maxLength={accountIdMaxLength}
                            dataQa="add-account-id"
                            icon={
                                <IconWithTooltip
                                    dataQa="account-id-tooltip"
                                    tooltipText={t('tooltips:accounts.accountId')}
                                    copy={false}
                                />
                            }
                            iconPosition="end"
                        />

                        <TextField
                            id="email"
                            label={t('screens:accounts.email')}
                            value={values.email}
                            onChange={handleChange}
                            setFieldError={setFieldError}
                            helperText={errors.email}
                            maxLength={accountEmailMaxLength}
                            dataQa="add-account-email"
                            icon={
                                <IconWithTooltip
                                    dataQa="account-email-tooltip"
                                    tooltipText={t('tooltips:accounts.email')}
                                    copy={false}
                                />
                            }
                            iconPosition="end"
                        />

                        <MainProductSelectField
                            fetchData={fetchData}
                            mainProductId={values.mainProductId}
                            setFieldError={setFieldError}
                            errorMsg={errors.mainProductId as string}
                            productType={MainProductType.Accounts}
                            onChange={(_, value) => setFieldValue('mainProductId', value?.i_product)}
                            required
                            disableInitSet
                        />

                        <KeyboardDateTimePicker
                            id="activationDate"
                            label={t('screens:accounts.activationDate')}
                            value={values.activationDate}
                            onChange={(val) => setFieldValue('activationDate', val ?? null)}
                            withHours={false}
                            allowEmptyDate
                            disablePast
                            minDate={minDate}
                            dataQa="add-account-activation-date"
                            helperText={errors.activationDate as string}
                            setFieldError={setFieldError}
                            emptyLabel={t('screens:accounts.immediately')}
                            userDateTimeFormat={userDateFormat}
                        />

                    </Grid>
                </form>
            </DialogContainer>
            <CreateNewExtensionErrorDialog
                isOpen={showErrorDialog}
                toggleVisibility={() => setShowErrorDialog(false)}
                customHeader={t('screens:accounts.errorHeader')}
                customDesc={t('screens:accounts.errorDesc')}
            />
        </>
    );
};

export default AddNewAccountDialog;